<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
        <oxd-text tag="span">
          {{ checkedItems.length }} empleado(s) seleccionado(s) para el reporte
        </oxd-text>
      </div>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div v-if="showPaginator" class="orangehrm-bottom-container">
        <oxd-pagination v-model:current="currentPage" :length="pages" />
      </div>
    </div>

    <br />

    <div class="orangehrm-paper-container">
      <oxd-form @submit-valid="onDownload">
        <oxd-form-row style="padding-top: 20px">
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item style="padding-left: 20px">
              <date-input
                v-model="dateRange.fromDate"
                placeholder="Desde"
                :rules="rules.fromDate"
                label="Rango de fechas"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="dateRange.toDate"
                label="&nbsp"
                placeholder="Hasta"
                :rules="rules.toDate"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions style="padding-bottom: 20px; padding-right: 20px">
          <oxd-button
            type="submit"
            display-type="secondary"
            icon-name="download"
            label="Descargar Excel"
            :disabled="checkedItems.length === 0"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useSort from '@ohrm/core/util/composable/useSort';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {APIService} from '@/core/util/services/api.service';
import {urlFor} from '@ohrm/core/util/helper/url';
import {
  required,
  validDateFormat,
  startDateShouldBeBeforeEndDate,
  endDateShouldBeAfterStartDate,
} from '@/core/util/validation/rules';

const defaultSortOrder = {
  'employee.employeeId': 'DEFAULT',
  'employee.firstName': 'ASC',
  'employee.lastName': 'DEFAULT',
};

export default {
  setup() {
    const {userDateFormat} = useDateFormat();

    const dataNormalizer = (data) => {
      return data.map((item) => ({
        id: item.empNumber,
        employeeId: item.employeeId,
        firstAndMiddleName: `${item.firstName} ${item.middleName}`,
        lastName: item.lastName,
        subunit: item.subunit?.name,
      }));
    };

    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });

    const serializedFilters = computed(() => {
      return {
        model: 'detailed',
        includeEmployees: 'onlyCurrent',
        sortField: sortField.value,
        sortOrder: sortOrder.value,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );
    const {showPaginator, currentPage, pages, response, isLoading, execQuery} =
      usePaginate(http, {
        query: serializedFilters,
        normalizer: dataNormalizer,
      });

    onSort(execQuery);

    const dateRange = ref({
      fromDate: null,
      toDate: null,
    });

    const rules = {
      fromDate: [
        required,
        validDateFormat(userDateFormat),
        startDateShouldBeBeforeEndDate(
          () => dateRange.value.toDate,
          'La fecha de inicio debe ser anterior a la fecha final',
          {allowSameDate: true},
        ),
      ],
      toDate: [
        required,
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => dateRange.value.fromDate,
          'La fecha final debe ser posterior a la fecha de inicio',
          {allowSameDate: true},
        ),
      ],
    };

    return {
      showPaginator,
      currentPage,
      isLoading,
      pages,
      items: response,
      sortDefinition,
      dateRange,
      rules,
    };
  },

  data() {
    return {
      checkedItems: [],
    };
  },

  computed: {
    headers() {
      return [
        {
          name: 'firstAndMiddleName',
          title: 'Nombre',
          sortField: 'employee.firstName',
          style: {flex: 1},
        },
        {
          name: 'lastName',
          title: 'Apellido',
          sortField: 'employee.lastName',
          style: {flex: 1},
        },
        {
          name: 'subunit',
          title: 'Subunidad',
          style: {flex: 1},
        },
      ];
    },
  },

  methods: {
    onDownload() {
      const empNumbers = this.checkedItems
        .map((index) => this.items?.data?.[index]?.id)
        .filter((id) => id !== undefined && id !== null);
      if (empNumbers.length === 0) {
        return;
      }
      const downUrl = urlFor(
        '/attendance/records/export',
        {},
        {
          empNumbers,
          fromDate: this.dateRange.fromDate,
          toDate: this.dateRange.toDate,
        },
      );
      window.open(downUrl, '_blank');
    },
  },
};
</script>
